import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, ShieldOff, ShieldCheck, MessageSquare, Search, Filter } from "lucide-react";

const STATUS = {
  open: { label: "Ouverte", color: "#E60000", bg: "#FEF2F2" },
  in_review: { label: "En cours", color: "#D97706", bg: "#FFFBEB" },
  resolved: { label: "Résolue", color: "#059669", bg: "#ECFDF5" },
  rejected: { label: "Rejetée", color: "#868E96", bg: "#F8F9FA" },
};

const CATEGORIES = {
  general: "Général",
  harcelement: "Harcèlement",
  salaire: "Salaire / Paie",
  securite: "Sécurité au travail",
  autre: "Autre",
};

export default function AssociationPlaintes() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [response, setResponse] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/association/plaintes");
      setItems(data);
      if (active) {
        const r = data.find((p) => p.id === active.id);
        if (r) setActive(r);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (newStatus) => {
    if (!active) return;
    try {
      await api.patch(`/association/plaintes/${active.id}`, { status: newStatus, response: response || undefined });
      toast.success("Plainte mise à jour");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveResponse = async () => {
    if (!active || !response.trim()) return;
    try {
      await api.patch(`/association/plaintes/${active.id}`, { response });
      toast.success("Réponse enregistrée");
      setResponse(""); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? items : items.filter((p) => p.status === filter);

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Modération"
        title="Plaintes salariés"
        sub="Gérez les plaintes déposées par les salariés des entreprises adhérentes."
        right={
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="plaintes-filter" className="h-11 w-44"><Filter className="h-3 w-3 mr-2" /><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes ({items.length})</SelectItem>
              {Object.entries(STATUS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label} ({items.filter((p) => p.status === k).length})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <div className="p-6 lg:p-10 grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5 bg-white border border-[#DEE2E6]" data-testid="plaintes-list">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">
              <AlertTriangle className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
              Aucune plainte {filter !== "all" && `(${STATUS[filter]?.label.toLowerCase()})`}.
            </div>
          ) : filtered.map((p) => {
            const st = STATUS[p.status] || STATUS.open;
            return (
              <button
                key={p.id}
                onClick={() => { setActive(p); setResponse(""); }}
                data-testid={`plainte-${p.id}`}
                className={`w-full text-left p-4 border-b border-[#DEE2E6] last:border-b-0 ${active?.id === p.id ? "bg-[#002FA7]/5 border-l-4 border-l-[#002FA7]" : "hover:bg-[#F8F9FA]"}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="font-semibold text-sm flex-1 truncate">{p.title}</div>
                  <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 shrink-0" style={{ background: st.bg, color: st.color }}>{st.label}</span>
                </div>
                <div className="text-[10px] text-[#868E96] mt-1 flex items-center gap-2 font-mono">
                  <span>{p.entreprise_name}</span>
                  <span>·</span>
                  <span>{CATEGORIES[p.category] || p.category}</span>
                  {p.anonymous && <><span>·</span><span className="flex items-center gap-0.5"><ShieldOff className="h-2.5 w-2.5" /> anonyme</span></>}
                </div>
                <div className="text-[10px] text-[#868E96] mt-0.5 font-mono">{new Date(p.created_at).toLocaleString("fr-FR")}</div>
              </button>
            );
          })}
        </div>

        <div className="lg:col-span-7 bg-white border border-[#DEE2E6] flex flex-col min-h-[600px]" data-testid="plainte-detail">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-[#868E96]">
              <div className="text-center">
                <Search className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
                Sélectionnez une plainte à gauche.
              </div>
            </div>
          ) : (
            <>
              <div className="p-5 border-b border-[#DEE2E6]">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#002FA7] font-bold flex items-center gap-2">
                  {CATEGORIES[active.category] || active.category} · {active.entreprise_name}
                </div>
                <h3 className="font-display text-xl font-bold mt-1">{active.title}</h3>
                <div className="text-xs text-[#868E96] mt-1 flex items-center gap-2">
                  {active.anonymous ? (
                    <span className="flex items-center gap-1"><ShieldOff className="h-3 w-3" /> Plainte anonyme</span>
                  ) : (
                    <span className="flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> {active.salarie_nom} ({active.salarie_code})</span>
                  )}
                  <span>·</span>
                  <span className="font-mono">{new Date(active.created_at).toLocaleString("fr-FR")}</span>
                </div>
              </div>

              <div className="p-5 flex-1 overflow-y-auto bg-[#F8F9FA]">
                <div className="bg-white border border-[#DEE2E6] p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-bold mb-2">Description</div>
                  <div className="text-sm whitespace-pre-wrap" data-testid="plainte-description">{active.description}</div>
                </div>

                {active.response && (
                  <div className="mt-4 bg-[#FFFBEB] border border-[#FCD34D] p-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#92400E] font-bold mb-2 flex items-center gap-2"><MessageSquare className="h-3 w-3" /> Réponse de l&apos;association</div>
                    <div className="text-sm whitespace-pre-wrap">{active.response}</div>
                  </div>
                )}

                <div className="mt-4 bg-white border border-[#DEE2E6] p-4">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#868E96] font-bold mb-2">{active.response ? "Modifier la réponse" : "Ajouter une réponse"}</div>
                  <Textarea value={response} onChange={(e) => setResponse(e.target.value)} rows={3} placeholder="Votre réponse au salarié et à l'entreprise…" data-testid="plainte-response" />
                  <Button onClick={saveResponse} disabled={!response.trim()} data-testid="plainte-save-response" className="mt-2 bg-[#002FA7] text-white">Enregistrer la réponse</Button>
                </div>
              </div>

              <div className="p-4 border-t border-[#DEE2E6] flex gap-2 flex-wrap">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#868E96] flex items-center mr-2">Changer le statut :</div>
                {Object.entries(STATUS).map(([k, v]) => (
                  <Button
                    key={k}
                    onClick={() => updateStatus(k)}
                    disabled={active.status === k}
                    data-testid={`plainte-status-${k}`}
                    variant="outline"
                    className="h-9 text-xs"
                    style={active.status === k ? { background: v.bg, borderColor: v.color, color: v.color } : {}}
                  >
                    {v.label}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
